import React, { useEffect, useCallback } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { 
  Table, 
  TableBody, 
  TableCell, 
  TableContainer, 
  TableHead, 
  TableRow, 
  Paper,
  Container,
  Typography,
  CircularProgress,
  Alert,
  Box,
  Avatar,
  useTheme,
  useMediaQuery
} from '@mui/material';
import { fetchUsers, fetchUserProgress } from '../../features/users/usersSlice';
import { useNavigate } from 'react-router-dom';

const Roster = () => {
  const dispatch = useDispatch();
  const { users, loading, error, userStats } = useSelector((state) => state.users);
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  useEffect(() => {
    dispatch(fetchUsers());
  }, [dispatch]);

  // Fetch all user stats at once when users are loaded
  useEffect(() => {
    if (users?.length > 0) {
      // Create a batch request for all users' stats
      const fetchAllStats = async () => {
        try {
          const promises = users.map(user => 
            dispatch(fetchUserProgress(user.id)).unwrap()
          );
          await Promise.all(promises);
        } catch (err) {
          console.error('Error fetching user stats:', err);
        }
      };
      fetchAllStats();
    }
  }, [dispatch, users]);

  const renderMobileUserCard = useCallback((user) => {
    const stats = userStats?.[user.id];
    
    return (
      <Paper
        key={user.id}
        sx={{
          p: 2,
          mb: 2,
          cursor: 'pointer',
          '&:hover': {
            bgcolor: 'rgba(0, 0, 0, 0.04)'
          }
        }}
        onClick={() => navigate(`/roster/${user.id}`)}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
          <Avatar sx={{ mr: 2 }}>{user.username[0].toUpperCase()}</Avatar>
          <Box>
            <Typography variant="subtitle1">{user.username}</Typography>
            <Typography variant="body2" color="text.secondary">
              {user?.belt_rank || '-'}
            </Typography>
          </Box>
        </Box>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 1 }}>
          <Box sx={{ textAlign: 'center' }}>
            <Typography variant="body2" color="text.secondary">Stripes</Typography>
            <Typography variant="h6">{stats?.stripes || '0'}</Typography>
          </Box>
          <Box sx={{ textAlign: 'center' }}>
            <Typography variant="body2" color="text.secondary">Techniques</Typography>
            <Typography variant="h6">{stats?.techniquesLearned || '0'}</Typography>
          </Box>
          <Box sx={{ textAlign: 'center' }}>
            <Typography variant="body2" color="text.secondary">Classes</Typography>
            <Typography variant="h6">{stats?.classesCompleted || '0'}</Typography>
          </Box>
        </Box>
      </Paper>
    );
  }, [navigate, userStats]);

  const renderDesktopTable = useCallback(() => (
    <TableContainer component={Paper}>
      <Table>
        <TableHead>
          <TableRow>
            <TableCell>Username</TableCell>
            <TableCell>Belt Rank</TableCell>
            <TableCell>Stripes</TableCell>
            <TableCell>Techniques Learned</TableCell>
            <TableCell>Classes Completed</TableCell>
            <TableCell>Academy</TableCell>
            <TableCell>Admin</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {users.map((user) => {
            const stats = userStats?.[user.id];
            return (
              <TableRow
                key={user.id}
                hover
                onClick={() => navigate(`/roster/${user.id}`)}
                sx={{ cursor: 'pointer' }}
              >
                <TableCell>
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <Avatar sx={{ mr: 2 }}>{user.username[0].toUpperCase()}</Avatar>
                    {user.username}
                  </Box>
                </TableCell>
                <TableCell>{user?.belt_rank || '-'}</TableCell>
                <TableCell>{stats?.stripes || '0'}</TableCell>
                <TableCell>{stats?.techniquesLearned || '0'}</TableCell>
                <TableCell>{stats?.classesCompleted || '0'}</TableCell>
                <TableCell>{user?.academy || '-'}</TableCell>
                <TableCell>{user?.is_superuser ? 'Yes' : 'No'}</TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </TableContainer>
  ), [users, userStats, navigate]);

  if (loading) {
    return (
      <Container sx={{ mt: 4, display: 'flex', justifyContent: 'center' }}>
        <CircularProgress />
      </Container>
    );
  }

  if (error) {
    return (
      <Container sx={{ mt: 4 }}>
        <Alert severity="error">
          <Typography variant="h6">Error Loading Roster</Typography>
          <Typography>{typeof error === 'string' ? error : JSON.stringify(error)}</Typography>
        </Alert>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      <Typography variant="h3" gutterBottom align="left">
        Roster
      </Typography>
      {users.length === 0 ? (
        <Alert severity="info">No users found</Alert>
      ) : (
        isMobile ? (
          <Box>
            {users.map(renderMobileUserCard)}
          </Box>
        ) : (
          renderDesktopTable()
        )
      )}
    </Container>
  );
};

export default Roster; 